import React, { useContext, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CustomModal, UiButton } from '@shared/ui';

import cls from './ExtraStep.module.css';
import { Form } from '../../../../../components/UI/Form/Form';
import { FormBlock } from '../../../../../components/UI/Form/FormBlock/FormBlock';
import { Select } from '../../../../../components/UI/Select/Select';
import { Input } from '../../../../../components/UI/TextInput/Input';
import { ModalContext } from '../AddPaymentModal';

export const ExtraStep = ({
    modalState,
    accountsSelectList,
    setModalState,
    onHideModal,
    payment,
    isCanDeleteUnsettled,
    deleteUndistributedPayment,
}) => {
    const { t } = useTranslation();
    const firstUpdate = useRef(true);

    const partnersSelectList = useSelector((state) => state.paymentReducer.partnersSelectList);

    const partnersListWithButton = [
        {
            title: t('ExtraStep.partnersListWithButton.title.null'),
            value: null,
        },
        {
            title: t('ExtraStep.partnersListWithButton.title.add'),
            isButton: true,
            buttonHandler: () => {
                setModalState((prevState) => ({
                    ...prevState,
                    isOpenPartners: true,
                }));
                setFormState((prevState) => ({ ...prevState, partners: null }));
            },
            value: '',
        },
        ...partnersSelectList,
    ];

    const {
        selectedPartners,
        selectedFirstStepAccount,
        selectFirstStepAccountHandler,
        selectPartnersHandler,
        inputHandler,
        formState,
        setFormState,
        errors,
        setErrors,
        mode,
        needUpdateExtraData,
        setNeedUpdateExtraData,
    } = useContext(ModalContext);

    useEffect(() => {
        if (firstUpdate.current) {
            if (
                needUpdateExtraData &&
                selectedPartners &&
                modalState.addPaymentMode !== 'distribution'
            ) {
                setFormState((prev) => ({
                    ...prev,
                    inn: selectedPartners.partner.inn,
                    kpp: selectedPartners.partner.kpp,
                    bik: selectedPartners.partner.bik,
                    accountNumber: selectedPartners.partner.current_account_number,
                    fullName: selectedPartners.partner.full_name,
                }));
            }
            firstUpdate.current = false;
        } else if (selectedPartners && modalState.addPaymentMode !== 'distribution') {
            setFormState((prev) => ({
                ...prev,
                inn: selectedPartners.partner.inn,
                kpp: selectedPartners.partner.kpp,
                bik: selectedPartners.partner.bik,
                accountNumber: selectedPartners.partner.current_account_number,
                fullName: selectedPartners.partner.full_name,
            }));
        }
    }, [selectedPartners]);

    useEffect(() => {
        if (needUpdateExtraData && payment && payment.payload) {
            setFormState((prev) => ({
                ...prev,
                inn: payment.payload.inn,
                kpp: payment.payload.kpp,
                bik: payment.payload.bik,
                accountNumber: payment.payload.accountNumber,
                fullName: payment.payload.name,
            }));
        }
    }, []);

    const onSubmitButton = () => {
        if (selectedFirstStepAccount?.account?.integration && modalState.isBetweenAccount) {
            setErrors((prev) => ({
                ...prev,
                receiverAccount: t('ExtraStep.onSubmitButton.receiverAccount'),
            }));
            return;
        }

        const requiredReceiveAccount =
            modalState.isBetweenAccount === false ? true : !!formState.firstStepAccount;
        if (
            formState.fullName &&
            formState.bik &&
            formState.accountNumber &&
            requiredReceiveAccount
        ) {
            setErrors((prev) => ({
                ...prev,
                accountNumber: null,
                bik: null,
                fullName: null,
                receiverAccount: null,
            }));
            setModalState((prevState) => ({ ...prevState, step: 2 }));
        } else {
            if (modalState.isBetweenAccount && !formState.firstStepAccount) {
                setErrors((prev) => ({
                    ...prev,
                    receiverAccount: t('ExtraStep.onSubmitButton.setErrors'),
                }));
            } else {
                setErrors((prev) => ({ ...prev, receiverAccount: null }));
            }

            if (!formState.fullName) {
                setErrors((prev) => ({
                    ...prev,
                    fullName: t('ExtraStep.onSubmitButton.setErrors'),
                }));
            } else {
                setErrors((prev) => ({ ...prev, fullName: null }));
            }
            if (!formState.bik) {
                setErrors((prev) => ({
                    ...prev,
                    bik: t('ExtraStep.onSubmitButton.setErrors'),
                }));
            } else {
                setErrors((prev) => ({ ...prev, bik: null }));
            }
            if (!formState.accountNumber) {
                setErrors((prev) => ({
                    ...prev,
                    accountNumber: t('ExtraStep.onSubmitButton.setErrors'),
                }));
            } else {
                setErrors((prev) => ({ ...prev, accountNumber: null }));
            }
        }
    };

    let isDisabledBlock = false;

    if (mode === 'distribution') {
        isDisabledBlock = true;
    }

    return (
        <>
            <CustomModal.Body>
                <div className={classNames(cls.stepList)}>
                    <span className={classNames(cls.stepListItem)}>
                        {modalState.mode === 'debit'
                            ? t('ExtraStep.stepList.debit')
                            : t('ExtraStep.stepList.credit')}
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M6.00005 10.8619L13.5286 3.33331L14.4715 4.27612L6.00005 12.7475L2.19531 8.94279L3.13812 7.99998L6.00005 10.8619Z"
                                fill="#27AE60"
                            />
                        </svg>
                    </span>
                    <span className={classNames(cls.stepListItem)}>
                        {modalState.mode === 'debit'
                            ? t('ExtraStep.stepListItem.debit')
                            : t('ExtraStep.stepListItem.credit')}
                    </span>
                </div>

                <Form>
                    <FormBlock>
                        {modalState.isBetweenAccount ? (
                            <div className={classNames(cls.blockWrapper)}>
                                <Select
                                    isSmartPosition
                                    selected={selectedFirstStepAccount || null}
                                    onChange={selectFirstStepAccountHandler}
                                    placeholder={
                                        modalState.mode === 'debit'
                                            ? t('ExtraStep.blockWrapper.debit')
                                            : t('ExtraStep.blockWrapper.credit')
                                    }
                                    title={
                                        modalState.mode === 'debit'
                                            ? t('ExtraStep.blockWrapper.debit')
                                            : t('ExtraStep.blockWrapper.credit')
                                    }
                                    isRequired
                                    isAccount
                                    options={accountsSelectList}
                                    error={errors.receiverAccount}
                                />
                            </div>
                        ) : (
                            <div className={classNames(cls.blockWrapper)}>
                                <Select
                                    isSmartPosition
                                    selected={selectedPartners || null}
                                    onChange={selectPartnersHandler}
                                    placeholder={t(
                                        'ExtraStep.blockWrapper.companyPartner.placeholder'
                                    )}
                                    title={t('ExtraStep.blockWrapper.companyPartner.title')}
                                    isRequired={false}
                                    options={partnersListWithButton}
                                    error={errors.correspondent}
                                />
                            </div>
                        )}
                        <div className={classNames(cls.blockWrapper)}>
                            <Input
                                isRequired
                                title={t('ExtraStep.blockWrapper.fullName.title')}
                                name="fullName"
                                value={formState.fullName}
                                onChange={(event) => inputHandler(event)}
                                error={errors.fullName}
                                isDisabled={isDisabledBlock}
                            />
                        </div>
                    </FormBlock>
                    <FormBlock>
                        <div className={classNames(cls.blockWrapper)}>
                            <Input
                                isRequired={false}
                                title={t('ExtraStep.blockWrapper.inn.title')}
                                name="inn"
                                value={formState.inn}
                                onChange={(event) => inputHandler(event)}
                                isDisabled={isDisabledBlock}
                            />
                        </div>
                        <div className={classNames(cls.blockWrapper)}>
                            <Input
                                isRequired={false}
                                title={t('ExtraStep.blockWrapper.kpp.title')}
                                name="kpp"
                                value={formState.kpp}
                                onChange={(event) => inputHandler(event)}
                                isDisabled={isDisabledBlock}
                            />
                        </div>
                    </FormBlock>
                    <FormBlock>
                        <div className={classNames(cls.blockWrapper)}>
                            <Input
                                isRequired
                                title={t('ExtraStep.blockWrapper.bik.title')}
                                name="bik"
                                value={formState.bik}
                                onChange={(event) => inputHandler(event)}
                                isDisabled={isDisabledBlock}
                                error={errors.bik}
                            />
                        </div>
                        <div className={classNames(cls.blockWrapper)}>
                            <Input
                                isRequired
                                title={t('ExtraStep.blockWrapper.accountNumber.title')}
                                name="accountNumber"
                                value={formState.accountNumber}
                                onChange={(event) => inputHandler(event)}
                                isDisabled={isDisabledBlock}
                                error={errors.accountNumber}
                            />
                        </div>
                    </FormBlock>
                </Form>
            </CustomModal.Body>
            <CustomModal.Footer>
                <div className={classNames(cls.footer)}>
                    <UiButton
                        onClick={onHideModal}
                        btnColor="secondary"
                        btnText={t('button.cancel.text')}
                    />
                    {isCanDeleteUnsettled && (
                        <UiButton
                            className={cls.deleteButton}
                            onClick={() => deleteUndistributedPayment?.()}
                            btnColor="danger"
                            btnText={t('button.delete.text')}
                        />
                    )}
                    <div className={classNames(cls.buttonGroup)}>
                        <UiButton
                            onClick={() => {
                                setModalState((prev) => ({ ...prev, step: 1 }));
                                setNeedUpdateExtraData(false);
                            }}
                            btnColor="secondary"
                            btnText={t('button.back.text')}
                        />
                        <UiButton onClick={onSubmitButton} btnText={t('button.next.text')} />
                    </div>
                </div>
            </CustomModal.Footer>
        </>
    );
};
